import {
  SuccessionFilterTypeDto,
  SuccessorChangeLogHistoryTypeDto,
  SuccessorColourDto,
} from '@/api/generated';

export type SuccessionTranslation = {
  addSuccessor: string;
  card: {
    age: string;
    averageRisk: string;
    careerPaths: string;
    comment: string;
    countryOfWork: string;
    delete: string;
    deleteNode: string;
    domesticMobility: string;
    editCareerPaths: string;
    editColor: string;
    editComment: string;
    functionalGrade: string;
    goToProfile: string;
    highRisk: string;
    history: string;
    internationalMobility: string;
    jobSeniority: string;
    lowRisk: string;
    mobility: string;
    months: string;
    no: string;
    personalGrade: string;
    pmc: string;
    positionLevel: {
      managerial: string;
      operational: string;
      strategic: string;
    };
    positionSeniority: string;
    region: string;
    reportsTo: string;
    riskOfLeaving: string;
    sop: string;
    workSeniority: string;
    years: string;
    yes: string;
  };
  changelog: {
    clearFilter: string;
    exportButton: string;
    exporting: string;
    exportTooltip: string;
    filteredBy: string;
    pageInfo: string;
  };
  excelReport: {
    addSuccessorButton: string;
    addSuccessorTooltip: string;
    exportButton: string;
    exportTooltip: string;
  };
  form: {
    careerPathFilters: string;
    clearFilters: string;
    clearing: string;
    collapseSearchBar: string;
    emptyState: {
      buttonText: string;
      description: string;
      hint: string;
      title: string;
    };
    expandSearchBar: string;
    feSideValues: {
      ageBucket: {
        BETWEEN_35_40: string;
        BETWEEN_40_55: string;
        GT_55: string;
        LT_35: string;
      };
      mobility: {
        INTERNATIONAL_NO: string;
        INTERNATIONAL_YES: string;
        NATIONAL_NO: string;
        NATIONAL_YES: string;
      };
      noRating: string;
    };
    fields: Record<SuccessionFilterTypeDto, string>;
    filters: string;
    filtersActive: string;
    highRiskOfLeaving: string;
    noFilters: string;
    noResults: string;
    resetButton: string;
    results: string;
    searchBarTitle: string;
    searchFor: {
      current: string;
      successor: string;
    };
    searchForDisplay: {
      current: string;
      successor: string;
    };
    searching: string;
    selectAll: string;
    textSearchFields: {
      externalEmployeeId: string;
      firstName: string;
      lastName: string;
    };
    toggleTooltip: {
      current: string;
      successor: string;
    };
    uploadButton: string;
    viewChangeLog: string;
  };
  functionalAreasDashboard: {
    contextMenu: {
      showEmployees: string;
      showSuccessors: string;
    };
    filters: {
      excellent: string;
      good: string;
      needsAttention: string;
    };
    functionalAreaPlaceholder: string;
    scrollToFunctionalAreas: string;
    scrollToPositions: string;
    sections: {
      functionalAreas: string;
      positions: string;
    };
    show: string;
    title: string;
  };
  headerDashboard: {
    greenMatrix: string;
    highRiskOfLeaving: string;
    olderOrEqualTo55YearsOld: string;
    ready1To2YearsForPromotion: string;
    ready3To5YearsForPromotion: string;
    readyForPromotion: string;
    readyNowForPromotion: string;
    readyUpTo1YearForPromotion: string;
    sectionTitle: string;
  };
  inPosition: string;
  legend: {
    colors: {
      green: string;
      grey: string;
      red: string;
      yellow: string;
    };
    exclamation: string;
    icons: {
      countryOfWork: string;
      domesticMobility: string;
      internationalMobility: string;
      managementTrainee: string;
      portugueseEmail: string;
    };
    title: string;
  };
  loadMore: string;
  modals: {
    addSuccessor: {
      cancelButton: string;
      fields: {
        comment: {
          label: string;
          placeholder: string;
        };
        employee: {
          label: string;
          placeholder: string;
        };
        main: string;
        position: {
          label: string;
          placeholder: string;
        };
        readiness: {
          label: string;
          placeholder: string;
        };
      };
      submitButton: string;
      title: string;
    };
    comment: {
      label: string;
      placeholder: string;
      title: string;
    };
    deleteSuccessor: {
      cancelButton: string;
      fields: {
        comment: {
          label: string;
          placeholder: string;
        };
      };
      submitButton: string;
      title: string;
    };
    editCareer: {
      title: string;
    };
    editSuccession: {
      editCareer: {
        addPath: {
          fields: {
            comment: {
              label: string;
              placeholder: string;
            };
            main: string;
            position: string;
            readiness: string;
          };
          submitButton: string;
        };
        addPathButton: string;
        deletePath: {
          comment: {
            label: string;
            placeholder: string;
          };
          submitButton: string;
        };
        editPath: {
          comment: string;
          submitButton: string;
        };
        name: {
          false: string;
          true: string;
        };
        noCareerPaths: string;
      };
      editColor: {
        color: Record<SuccessorColourDto, string>;
        colorLabel: string;
        comment: {
          label: string;
          placeholder: string;
        };
        name: string;
        submitButton: string;
      };
      editExclamationMark: {
        comment: {
          label: string;
          placeholder: string;
        };
        exclamationMark: string;
        name: string;
        submitButton: string;
      };
      title: string;
    };
    history: {
      by: string;
      noHistory: string;
      title: string;
      type: Record<SuccessorChangeLogHistoryTypeDto, string>;
    };
  };
  noHistory: {
    description: string;
    title: string;
  };
  organizationChart: {
    buttons: {
      collapseAll: string;
      expandAll: string;
      search: string;
    };
    deleteNode: {
      buttons: {
        cancel: string;
        confirm: string;
      };
      hierarchyType: {
        organizational: string;
        personal: string;
      };
      prompt: {
        employeeAction: string;
        leaf: string;
        strategy: string;
      };
      strategies: {
        convertToVacancy: { description: string; label: string };
        deleteEmployee: { description: string; label: string };
        deleteWithChildren: { description: string; label: string };
        promoteChildren: { description: string; label: string };
      };
      title: string;
      warning: string;
    };
    displaySettings: {
      applyToLevel: string;
      applyToNode: string;
      fields: {
        age: string;
        flags: string;
        functionalGrade: string;
        matrix: string;
        monthsInCompany: string;
        monthsInCurrentPosition: string;
        personalGrade: string;
        photo: string;
        positionName: string;
        riskOfLeaving: string;
      };
      groups: {
        basic: string;
        details: string;
        indicators: string;
      };
      hideAll: string;
      nodeTitle: string;
      resetToDefault: string;
      showAll: string;
      title: string;
    };
    emptySimulation: {
      addFirstElement: string;
      description: string;
      title: string;
    };
    emptyState: {
      description: string;
      title: string;
    };
    legend: {
      addMode: {
        instructions: {
          greenBorderParent: string;
          useSidePanelButtons: string;
          yellowBorderChild: string;
        };
        selectChildren: string;
        selectParent: string;
      };
      normalMode: {
        clickToExpand: string;
        dragBackground: string;
        dragNode: string;
      };
    };
    nodeLabels: {
      nodePrefix: string;
      subunitsCount: string;
      vacancy: string;
    };
    nodeListDrawer: {
      noResults: string;
      searchHint: string;
      searchPlaceholder: string;
      title: string;
      totalCount: string;
    };
    search: {
      placeholder: string;
    };
    searchNode: {
      foundCount: string;
      nodeType: {
        employee: string;
        unit: string;
        vacancy: string;
      };
      noResults: string;
      noResultsPersonal: string;
      previewButton: string;
      searchHint: string;
      searchHintPersonal: string;
      searchPlaceholder: string;
      searchPlaceholderPersonal: string;
      title: string;
      titlePersonal: string;
    };
    title: string;
    tooltips: {
      addNode: string;
      collapse: string;
      deleteNode: string;
      expand: string;
      goToProfile: string;
    };
    wrapper: {
      createNodeError: string;
      loadingStructure: string;
      nodeCreated: string;
      rootNodesError: string;
      simulationsError: string;
      unknownTree: string;
    };
  };
  profilePictureModal: {
    cancel: string;
    delete: string;
    deleteError: string;
    deleteSuccess: string;
    deleting: string;
    dropImage: string;
    save: string;
    selectDifferentImage: string;
    supportedFormats: string;
    title: string;
    uploadError: string;
    uploadImage: string;
    uploading: string;
    uploadSuccess: string;
    zoom: string;
  };
  simulation: {
    accessModal: {
      addUsers: string;
      buttons: {
        cancel: string;
        updateAccess: string;
        updating: string;
      };
      description: string;
      searchPlaceholder: string;
      selectedUsers: string;
      simulationName: string;
      title: string;
    };
    addNode: {
      sidePanel: {
        alerts: {
          employeeRequired: string;
          invalidPosition: string;
          positionRequired: string;
          selectParent: string;
          unitNameRequired: string;
          vacancyNameRequired: string;
          validationError: string;
        };
        buttons: {
          add: {
            organizational: string;
            personal: string;
          };
          adding: string;
          cancel: string;
        };
        children: {
          label: {
            organizational: string;
            personal: string;
          };
        };
        fields: {
          position: {
            createNew: string;
            creating: string;
            freeSoloHint: string;
            label: string;
            noOptions: string;
            required: string;
          };
          searchEmployee: {
            hint: string;
            label: string;
            loading: string;
            noOptions: string;
            placeholder: string;
            required: string;
          };
          unitName: {
            label: string;
            required: string;
          };
        };
        nodeType: {
          employee: string;
          label: string;
          organizational: string;
          vacancy: string;
        };
        parent: {
          label: {
            organizational: string;
            personal: string;
          };
          rootNode: string;
          selectButton: {
            organizational: string;
            personal: string;
          };
          selectingButton: string;
        };
        selectedNodes: string;
        title: string;
      };
      tooltip: string;
    };
    createModal: {
      buttons: {
        cancel: string;
        create: string;
        creating: string;
      };
      fields: {
        description: {
          label: string;
          placeholder: string;
        };
        empty: {
          label: string;
          options: {
            copyStructure: string;
            emptySimulation: string;
          };
        };
        name: {
          label: string;
          placeholder: string;
        };
        type: {
          label: string;
          options: {
            organizational: {
              description: string;
              label: string;
            };
            personal: {
              description: string;
              label: string;
            };
          };
        };
      };
      title: string;
    };
    editModal: {
      buttons: {
        cancel: string;
        update: string;
        updating: string;
      };
      fields: {
        description: {
          label: string;
          placeholder: string;
        };
        name: {
          label: string;
          placeholder: string;
        };
      };
      title: string;
    };
    rootNodeSelector: {
      defaultOption: string;
      label: string;
      searchSupervisorPlaceholder: string;
      searchUnitPlaceholder: string;
      subunitsText: string;
      viewDefault: string;
      viewSupervisor: string;
      viewUnit: string;
    };
    simulationSelector: {
      label: string;
      main: {
        chip: string;
        text: string;
      };
      simulation: {
        chip: string;
        createdText: string;
      };
    };
    switchTreeDialog: {
      cancel: string;
      confirm: string;
      currentTree: string;
      description: string;
      question: string;
      setAsRoot: string;
      subtitle: string;
      targetTree: string;
      title: string;
    };
    toolbar: {
      newSimulation: string;
    };
  };
  simulationManagement: {
    buttons: {
      cancel: string;
      filters: string;
      filtersActive: string;
      newSimulation: string;
      reset: string;
      retry: string;
      showAll: string;
    };
    confirmDeleteSimulation: string;
    currentStructures: string;
    errors: {
      deleteSimulation: string;
      loadingData: string;
      updateDetails: string;
      updateStatus: string;
    };
    filters: {
      active: string;
      allStatuses: string;
      allTypes: string;
      archived: string;
      deleted: string;
      organizational: string;
      personal: string;
    };
    initialView: {
      description: string;
      title: string;
    };
    menu: {
      delete: string;
      edit: string;
      manageAccess: string;
    };
    pagination: {
      showing: string;
    };
    search: {
      placeholder: string;
    };
    sidebar: {
      applyButton: string;
      resetButton: string;
      searchLabel: string;
      statusLabel: string;
      title: string;
      typeLabel: string;
    };
    status: {
      active: string;
      archived: string;
      deleted: string;
      unknown: string;
    };
    table: {
      empty: {
        noData: string;
        noResults: string;
      };
      headers: {
        access: string;
        actions: string;
        author: string;
        created: string;
        description: string;
        lastModified: string;
        name: string;
        status: string;
        type: string;
      };
    };
    types: {
      organizational: string;
      personal: string;
    };
  };
  successorsForPosition: string;
  tabs: {
    changelog: string;
    dashboard: string;
    filters: string;
    nestedStructure: string;
    organizationChart: string;
  };
};

export const successionTranslation: SuccessionTranslation = {
  addSuccessor: 'Add New Successor',
  card: {
    age: 'Age',
    averageRisk: 'medium',
    careerPaths: 'CAREER PATHS',
    comment: 'Comment',
    countryOfWork: 'Country of work',
    delete: 'Delete successor',
    deleteNode: 'Delete element',
    domesticMobility: 'Dom. Mobility',
    editCareerPaths: 'Edit career paths',
    editColor: 'Edit Color Status',
    editComment: 'Edit comment',
    functionalGrade: 'FG',
    goToProfile: 'Go to profile',
    highRisk: 'high',
    history: 'View history',
    internationalMobility: 'Int. Mobility',
    jobSeniority: 'Job',
    lowRisk: 'low',
    mobility: 'Mobility',
    months: 'm',
    no: 'No',
    personalGrade: 'PG',
    pmc: 'PMC',
    positionLevel: {
      managerial: 'Managerial',
      operational: 'Operational',
      strategic: 'Strategic',
    },
    positionSeniority: 'Position Seniority',
    region: 'Region',
    reportsTo: 'Reports to',
    riskOfLeaving: 'Risk of Leaving',
    sop: 'SOP',
    workSeniority: 'JM',
    years: 'y',
    yes: 'Yes',
  },
  changelog: {
    clearFilter: 'Clear filter',
    exportButton: 'Export History',
    exporting: 'Exporting...',
    exportTooltip: 'Export succession change log history to Excel file',
    filteredBy: 'Employee history',
    pageInfo: 'Page {{page}} of {{total}}',
  },
  excelReport: {
    addSuccessorButton: 'Add Successor',
    addSuccessorTooltip: 'Add new successor to selected position',
    exportButton: 'Export Excel',
    exportTooltip: 'Export succession report to Excel file',
  },
  form: {
    careerPathFilters: 'Search in career path',
    clearFilters: 'Clear',
    clearing: 'Clearing...',
    collapseSearchBar: 'Collapse search bar',
    emptyState: {
      buttonText: 'Open search filters',
      description:
        'Use advanced filters to find successors for key positions in the organization or check employees currently ready for promotion.',
      hint: 'You can also search by position, region, readiness and other criteria',
      title: 'Start succession planning',
    },
    expandSearchBar: 'Expand search bar',
    feSideValues: {
      ageBucket: {
        BETWEEN_35_40: '35-40',
        BETWEEN_40_55: '41-55',
        GT_55: 'Over 55',
        LT_35: 'Under 35',
      },
      mobility: {
        INTERNATIONAL_NO: 'International: no',
        INTERNATIONAL_YES: 'International: yes',
        NATIONAL_NO: 'National: no',
        NATIONAL_YES: 'National: yes',
      },
      noRating: 'No rating',
    },
    fields: {
      [SuccessionFilterTypeDto.AgeBucket]: 'Age',
      [SuccessionFilterTypeDto.Department]: 'Department',
      [SuccessionFilterTypeDto.EmployeeSuccessionReadiness]: 'Readiness',
      [SuccessionFilterTypeDto.FunctionalArea]: 'Functional Area',
      [SuccessionFilterTypeDto.FunctionalSupervisor]: 'Functional Supervisor',
      [SuccessionFilterTypeDto.Grade]: 'Grade',
      [SuccessionFilterTypeDto.HierarchicalSupervisor]: 'Hierarchical Supervisor',
      [SuccessionFilterTypeDto.JobLevel]: 'Job Level',
      [SuccessionFilterTypeDto.Matrix]: 'Field on the Matrix',
      [SuccessionFilterTypeDto.Mobility]: 'Mobility',
      [SuccessionFilterTypeDto.Mt]: 'Management Trainee – year',
      [SuccessionFilterTypeDto.PmcRating]: 'PMC Rating',
      [SuccessionFilterTypeDto.Position]: 'Position',
      [SuccessionFilterTypeDto.Region]: 'Region',
      [SuccessionFilterTypeDto.SopRating]: 'SOP Rating',
      [SuccessionFilterTypeDto.SuccessionDepartment]: 'Department',
      [SuccessionFilterTypeDto.SuccessionPosition]: 'Position',
      [SuccessionFilterTypeDto.Zone]: 'Zone',
    },
    filters: 'Filters',
    filtersActive: 'Active Filters',
    highRiskOfLeaving: 'Show employees with high risk of leaving',
    noFilters: 'No filters',
    noResults: 'No results',
    resetButton: 'Reset',
    results: 'results',
    searchBarTitle: 'Search Filters',
    searchFor: {
      current: 'Employees in position',
      successor: 'Successors for position',
    },
    searchForDisplay: {
      current: 'Browsing employees in positions',
      successor: 'Browsing successors for positions',
    },
    searching: 'Searching...',
    selectAll: 'Select All',
    textSearchFields: {
      externalEmployeeId: 'SAP Number',
      firstName: 'First Name',
      lastName: 'Last Name',
    },
    toggleTooltip: {
      current: 'Click to switch to: Browsing employees in positions',
      successor: 'Click to switch to: Browsing successors for positions',
    },
    uploadButton: 'Search',
    viewChangeLog: 'View Change Log',
  },
  functionalAreasDashboard: {
    contextMenu: {
      showEmployees: 'Show Employees',
      showSuccessors: 'Show Successors',
    },
    filters: {
      excellent: 'Excellent',
      good: 'Good',
      needsAttention: 'Needs Attention',
    },
    functionalAreaPlaceholder: 'Functional Area',
    scrollToFunctionalAreas: 'Functional Areas',
    scrollToPositions: 'Positions',
    sections: {
      functionalAreas: 'Functional Areas',
      positions: 'Positions',
    },
    show: 'Show:',
    title: 'Dashboard',
  },
  headerDashboard: {
    greenMatrix: 'Leadership Potential',
    highRiskOfLeaving: 'High Risk of Leaving',
    olderOrEqualTo55YearsOld: '55+ Years Old',
    ready1To2YearsForPromotion: 'Ready 1-2 Years',
    ready3To5YearsForPromotion: 'Ready 3-5 Years',
    readyForPromotion: 'Ready for Promotion',
    readyNowForPromotion: 'Ready Now',
    readyUpTo1YearForPromotion: 'Ready Up To 1 Year',
    sectionTitle: 'Succession Statistics',
  },
  inPosition: 'in position',
  legend: {
    colors: {
      green: 'Employee ready to take on more responsible role',
      grey: 'Employee',
      red: 'Alert',
      yellow: 'Employee to watch',
    },
    exclamation: 'High Risk of Leaving',
    icons: {
      countryOfWork: 'Country of work',
      domesticMobility: 'ExComm Pipeline',
      internationalMobility: 'Expat Pipeline',
      managementTrainee: 'Trainee',
      portugueseEmail: 'Global assignment',
    },
    title: 'Legend',
  },
  loadMore: 'Show more',
  modals: {
    addSuccessor: {
      cancelButton: 'Cancel',
      fields: {
        comment: {
          label: 'Comment',
          placeholder: 'Reason for adding successor',
        },
        employee: {
          label: 'Choose Employee',
          placeholder: "Search by SAP or employee's name",
        },
        main: 'Set this career path as main',
        position: {
          label: 'Position',
          placeholder: 'Select position',
        },
        readiness: {
          label: 'Readiness',
          placeholder: 'Select readiness level',
        },
      },
      submitButton: 'Add Successor',
      title: 'Add Successor',
    },
    comment: {
      label: 'Comment',
      placeholder: 'Add a comment',
      title: 'Update comment',
    },
    deleteSuccessor: {
      cancelButton: 'Cancel',
      fields: {
        comment: {
          label: 'Comment',
          placeholder: 'Reason for deletion',
        },
      },
      submitButton: 'Delete Successor',
      title: 'Delete Successor',
    },
    editCareer: {
      title: 'Edit Career Path',
    },
    editSuccession: {
      editCareer: {
        addPath: {
          fields: {
            comment: {
              label: 'Comment',
              placeholder: 'Reason for adding career path',
            },
            main: 'Main',
            position: 'Position',
            readiness: 'Readiness',
          },
          submitButton: 'Add Career Path',
        },
        addPathButton: 'Add Career Path',
        deletePath: {
          comment: {
            label: 'Comment',
            placeholder: 'Reason for removing career path',
          },
          submitButton: 'Remove Path',
        },
        editPath: {
          comment: 'Reason for editing career path',
          submitButton: 'Save Changes',
        },
        name: {
          false: 'Edit Path',
          true: 'Edit Career Paths',
        },
        noCareerPaths: 'No career paths available',
      },
      editColor: {
        color: {
          [SuccessorColourDto.Green]: 'Green',
          [SuccessorColourDto.Grey]: 'Grey',
          [SuccessorColourDto.Red]: 'Red',
          [SuccessorColourDto.Yellow]: 'Yellow',
        },
        colorLabel: 'Color Status',
        comment: {
          label: 'Comment',
          placeholder: 'Reason for status change',
        },
        name: 'Edit Color Status',
        submitButton: 'Update Color',
      },
      editExclamationMark: {
        comment: {
          label: 'Comment',
          placeholder: 'Reason for status change',
        },
        exclamationMark: 'Show Exclamation Mark',
        name: 'Edit Exclamation Mark status',
        submitButton: 'Change Status',
      },
      title: 'Edit Successor',
    },
    history: {
      by: 'By',
      noHistory: 'No change history available',
      title: 'Change History',
      type: {
        [SuccessorChangeLogHistoryTypeDto.CareerPathAdd]: 'New Career Path Added',
        [SuccessorChangeLogHistoryTypeDto.CareerPathDelete]: 'Career Path Removed',
        [SuccessorChangeLogHistoryTypeDto.CareerPathUpdate]: 'Career Path Updated',
        [SuccessorChangeLogHistoryTypeDto.ColourUpdate]: 'Color Status Updated',
        [SuccessorChangeLogHistoryTypeDto.EmployeeCommentUpdate]: 'Employee Comment Updated',
        [SuccessorChangeLogHistoryTypeDto.ExclamationMarkUpdate]: 'Exclamation Mark Updated',
        [SuccessorChangeLogHistoryTypeDto.ExcommUpdate]: 'ExComm Pipeline Status Updated',
        [SuccessorChangeLogHistoryTypeDto.ExpatUpdate]: 'Expat Pipeline Status Updated',
      },
    },
  },
  noHistory: {
    description: 'There are no changes recorded in the succession history yet.',
    title: 'No History Available',
  },
  organizationChart: {
    buttons: {
      collapseAll: 'Collapse All',
      expandAll: 'Expand All',
      search: 'Search',
    },
    deleteNode: {
      buttons: {
        cancel: 'Cancel',
        confirm: 'Delete node',
      },
      hierarchyType: {
        organizational: 'the organizational hierarchy',
        personal: 'the personal hierarchy',
      },
      prompt: {
        employeeAction: 'Choose what happens to this employee:',
        leaf: 'Are you sure you want to remove this node from {{hierarchy}}?',
        strategy: 'Choose how to remove this node from {{hierarchy}}:',
      },
      strategies: {
        convertToVacancy: {
          description:
            'The employee node becomes a vacancy. The hierarchy structure stays untouched.',
          label: 'Convert employee to a vacancy',
        },
        deleteEmployee: {
          description: 'The employee node is removed from the hierarchy. This cannot be undone.',
          label: 'Delete the node',
        },
        deleteWithChildren: {
          description:
            'The node and all of its descendants are removed from the hierarchy. This cannot be undone.',
          label: 'Delete the node with all descendants',
        },
        promoteChildren: {
          description:
            'The node is removed and its children are reattached to its parent. Hierarchy levels are recalculated.',
          label: 'Delete the node and promote its children',
        },
      },
      title: 'Delete node',
      warning: 'This operation cannot be undone.',
    },
    displaySettings: {
      applyToLevel: 'Siblings',
      applyToNode: 'This employee only',
      fields: {
        age: 'Age',
        flags: 'Flags (country, expat)',
        functionalGrade: 'Functional Grade',
        matrix: 'Matrix',
        monthsInCompany: 'JM Seniority',
        monthsInCurrentPosition: 'Time in Current Position',
        personalGrade: 'Personal Grade',
        photo: 'Photo',
        positionName: 'Position',
        riskOfLeaving: 'Risk of Leaving',
      },
      groups: {
        basic: 'Basic',
        details: 'Details',
        indicators: 'Indicators',
      },
      hideAll: 'Hide all',
      nodeTitle: 'Node display settings',
      resetToDefault: 'Reset to default',
      showAll: 'Show all',
      title: 'Display Settings',
    },
    emptySimulation: {
      addFirstElement: 'Add First Element',
      description:
        'This simulation is empty. Add the first element to start building the structure.',
      title: 'Empty Simulation',
    },
    emptyState: {
      description: 'Go to "Manage Structures" tab to display and select a structure.',
      title: 'Select Structure',
    },
    legend: {
      addMode: {
        instructions: {
          greenBorderParent: 'Green border = supervisor',
          useSidePanelButtons: 'Use side panel buttons to select nodes',
          yellowBorderChild: 'Yellow border = subordinate employees',
        },
        selectChildren: 'Select subordinate employees node (yellow frame)',
        selectParent: 'Select the supervisor node (green frame)',
      },
      normalMode: {
        clickToExpand: 'Click node to expand/collapse',
        dragBackground: 'Drag background - move view',
        dragNode: 'Drag node to move',
      },
    },
    nodeLabels: {
      nodePrefix: 'Node',
      subunitsCount: 'subunits',
      vacancy: 'Open position',
    },
    nodeListDrawer: {
      noResults: 'No nodes found',
      searchHint: 'Enter at least 2 characters to search',
      searchPlaceholder: 'Search by name...',
      title: 'Node List',
      totalCount: 'nodes',
    },
    search: {
      placeholder: 'Search...',
    },
    searchNode: {
      foundCount: 'Found',
      nodeType: {
        employee: 'Employee',
        unit: 'Organizational Unit',
        vacancy: 'Vacant Position',
      },
      noResults: 'No units found matching the search',
      noResultsPersonal: 'No people found matching the search',
      previewButton: 'Show on chart',
      searchHint: 'Enter unit name to start searching (min. 2 characters)',
      searchHintPersonal: 'Enter employee name to start searching (min. 2 characters)',
      searchPlaceholder: 'Unit name...',
      searchPlaceholderPersonal: 'Employee name, position...',
      title: 'Search Unit',
      titlePersonal: 'Search Person',
    },
    title: 'JM Polska S.A. - Organizational Structure',
    tooltips: {
      addNode: 'Add node',
      collapse: 'Collapse',
      deleteNode: 'Delete node',
      expand: 'Expand',
      goToProfile: 'Go to profile',
    },
    wrapper: {
      createNodeError: 'The node could not be added.',
      loadingStructure: 'Loading the structure…',
      nodeCreated: 'The node was added to the structure.',
      rootNodesError: 'Starting points could not be loaded.',
      simulationsError: 'Simulations could not be loaded',
      unknownTree: 'Unknown',
    },
  },
  profilePictureModal: {
    cancel: 'Cancel',
    delete: 'Delete Picture',
    deleteError: 'Failed to delete avatar. Please try again.',
    deleteSuccess: 'Avatar deleted successfully!',
    deleting: 'Deleting...',
    dropImage: 'Drop the image here',
    save: 'Save Picture',
    selectDifferentImage: 'Select Different Photo',
    supportedFormats: 'Supported formats: JPEG, PNG, GIF, WebP',
    title: 'Upload Profile Picture',
    uploadError: 'Failed to upload avatar. Please try again.',
    uploadImage: 'Click or drag to upload a photo',
    uploading: 'Uploading...',
    uploadSuccess: 'Avatar uploaded successfully!',
    zoom: 'Zoom',
  },
  simulation: {
    accessModal: {
      addUsers: 'Add Users',
      buttons: {
        cancel: 'Cancel',
        updateAccess: 'Update Access',
        updating: 'Updating...',
      },
      description:
        'Control who can edit and manage this simulation. The simulation owner always has full access.',
      searchPlaceholder: 'Search users by name or email...',
      selectedUsers: 'Users with access',
      simulationName: 'Simulation',
      title: 'Manage Simulation Access',
    },
    addNode: {
      sidePanel: {
        alerts: {
          employeeRequired: 'You must select an employee.',
          invalidPosition: 'Selected position is invalid.',
          positionRequired: 'Position is required for vacancies.',
          selectParent: 'You must select a parent node before adding a new node.',
          unitNameRequired: 'Header is required for organizational units.',
          vacancyNameRequired: 'Header is required for vacancies.',
          validationError: 'A validation error occurred. Please check the entered data.',
        },
        buttons: {
          add: {
            organizational: 'Add',
            personal: 'Add',
          },
          adding: 'Adding...',
          cancel: 'Cancel',
        },
        children: {
          label: {
            organizational: 'Subunits',
            personal: 'Subordinates',
          },
        },
        fields: {
          position: {
            createNew: 'Create',
            creating: 'Adding the new position…',
            freeSoloHint: 'Type a new position and it will be added automatically',
            label: 'Position',
            noOptions: 'No options',
            required: 'Position is required',
          },
          searchEmployee: {
            hint: 'Type first name, last name or SAP ID to search',
            label: 'Search Employee',
            loading: 'Searching...',
            noOptions: 'No results',
            placeholder: 'First name, last name or SAP...',
            required: 'Employee is required',
          },
          unitName: {
            label: 'Add header',
            required: 'Header is required',
          },
        },
        nodeType: {
          employee: 'Employee',
          label: 'Add:',
          organizational: 'Organizational Unit',
          vacancy: 'Vacancy',
        },
        parent: {
          label: {
            organizational: 'Unit:',
            personal: 'Supervisor:',
          },
          rootNode: 'Root Element',
          selectButton: {
            organizational: 'Click to select Unit',
            personal: 'Click to select Supervisor',
          },
          selectingButton: 'Click node on chart...',
        },
        selectedNodes: 'Selected Nodes:',
        title: '',
      },
      tooltip: 'Add new node to structure',
    },
    createModal: {
      buttons: {
        cancel: 'Cancel',
        create: 'Create Simulation',
        creating: 'Creating...',
      },
      fields: {
        description: {
          label: 'Description',
          placeholder: 'Add simulation description...',
        },
        empty: {
          label: 'Simulation Creation Type',
          options: {
            copyStructure: 'Copy current structure',
            emptySimulation: 'Empty simulation',
          },
        },
        name: {
          label: 'Simulation Name',
          placeholder: 'Enter simulation name...',
        },
        type: {
          label: 'Simulation Type *',
          options: {
            organizational: {
              description: 'Simulation of organizational structure changes',
              label: 'Organizational',
            },
            personal: {
              description: 'Simulation of personnel changes',
              label: 'Personal',
            },
          },
        },
      },
      title: 'Create New Simulation',
    },
    editModal: {
      buttons: {
        cancel: 'Cancel',
        update: 'Update Simulation',
        updating: 'Updating...',
      },
      fields: {
        description: {
          label: 'Description (optional)',
          placeholder: 'Update simulation description...',
        },
        name: {
          label: 'Simulation Name',
          placeholder: 'Update simulation name...',
        },
      },
      title: 'Edit Simulation',
    },
    rootNodeSelector: {
      defaultOption: 'JM Polska S.A. (default)',
      label: 'Starting Point:',
      searchSupervisorPlaceholder: 'Search supervisor...',
      searchUnitPlaceholder: 'Search unit...',
      subunitsText: 'subunits',
      viewDefault: 'Standard',
      viewSupervisor: 'By Supervisor',
      viewUnit: 'By Unit',
    },
    simulationSelector: {
      label: 'View:',
      main: {
        chip: 'MAIN',
        text: 'Organizational Structure (current)',
      },
      simulation: {
        chip: 'SIM',
        createdText: 'Created:',
      },
    },
    switchTreeDialog: {
      cancel: 'Cancel',
      confirm: 'Switch to full tree',
      currentTree: 'Current tree:',
      description: 'The selected node is in a different organizational tree:',
      question: 'What would you like to do?',
      setAsRoot: 'Set as starting point',
      subtitle: 'Selected node belongs to different tree',
      targetTree: 'Target tree:',
      title: 'Switch Organizational Tree',
    },
    toolbar: {
      newSimulation: 'New Simulation',
    },
  },
  simulationManagement: {
    buttons: {
      cancel: 'Cancel',
      filters: 'Filters',
      filtersActive: 'Active Filters',
      newSimulation: 'New Simulation',
      reset: 'Reset',
      retry: 'Try again',
      showAll: 'Show All',
    },
    confirmDeleteSimulation:
      'Are you sure you want to permanently delete this simulation? This operation is irreversible and will remove all data associated with the simulation.',
    currentStructures: 'Current structures:',
    errors: {
      deleteSimulation: 'An error occurred while deleting simulation',
      loadingData: 'An error occurred while loading data',
      updateDetails: 'An error occurred while updating simulation details',
      updateStatus: 'An error occurred while updating status',
    },
    filters: {
      active: 'Active',
      allStatuses: 'All statuses',
      allTypes: 'All types',
      archived: 'Archived',
      deleted: 'Deleted',
      organizational: 'Organizational',
      personal: 'Personal',
    },
    initialView: {
      description: 'Choose search option',
      title: 'Simulation Management',
    },
    menu: {
      delete: 'Delete',
      edit: 'Edit',
      manageAccess: 'Manage access',
    },
    pagination: {
      showing: 'Showing {{shown}} of {{total}} simulations',
    },
    search: {
      placeholder: 'Search simulations...',
    },
    sidebar: {
      applyButton: 'Apply Filters',
      resetButton: 'Clear Filters',
      searchLabel: 'Text Search',
      statusLabel: 'Simulation Status',
      title: 'Search Filters',
      typeLabel: 'Simulation Type',
    },
    status: {
      active: 'Active',
      archived: 'Archived',
      deleted: 'Deleted',
      unknown: 'Unknown',
    },
    table: {
      empty: {
        noData: 'No simulations to display yet',
        noResults: 'No simulation matches the selected filters',
      },
      headers: {
        access: 'Access',
        actions: 'Actions',
        author: 'Author',
        created: 'Created',
        description: 'Description',
        lastModified: 'Last Modified',
        name: 'Name',
        status: 'Status',
        type: 'Type',
      },
    },
    types: {
      organizational: 'Organizational',
      personal: 'Personal',
    },
  },
  successorsForPosition: 'successors',
  tabs: {
    changelog: 'History',
    dashboard: 'Dashboard',
    filters: 'Search',
    nestedStructure: 'Nested Structure',
    organizationChart: 'Organizational Structure',
  },
};
